/**
 * Gradient tokens - Theme-aware gradient system
 * Provides consistent gradients across all themes
 */

import { ThemeColors } from '../../assets/theme';
import { generateGradient, generateRadialGradient } from '../../utils/colorUtils';

export type GradientDirection = 
  | 'to-right' 
  | 'to-bottom' 
  | 'to-left' 
  | 'to-top'
  | 'to-bottom-right'
  | 'to-bottom-left'
  | 'to-top-right'
  | 'to-top-left'
  | string; // For custom angles like '135deg'

export interface GradientPreset {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  overlay: string;
}

/**
 * Convert direction keyword to CSS angle
 */
const directionToAngle = (direction: GradientDirection): string => {
  const map: Record<string, string> = {
    'to-right': '90deg',
    'to-bottom': '180deg',
    'to-left': '270deg',
    'to-top': '0deg',
    'to-bottom-right': '135deg',
    'to-bottom-left': '225deg',
    'to-top-right': '45deg',
    'to-top-left': '315deg',
  };
  return map[direction] || direction;
};

/**
 * Generate gradient presets from theme colors
 */
export const getGradientPresets = (colors: ThemeColors): GradientPreset => {
  return {
    primary: generateGradient(colors.primary, colors.primaryLight, '135deg'),
    secondary: generateGradient(colors.secondary, colors.secondaryLight, '135deg'),
    accent: generateGradient(colors.accent, colors.accentLight, '135deg'),
    background: generateGradient(colors.background, colors.surface, '180deg'),
    surface: generateGradient(colors.surface, colors.surfaceHover, '180deg'),
    overlay: generateGradient(
      `rgba(0, 0, 0, 0.1)`,
      `rgba(0, 0, 0, 0.3)`,
      '180deg'
    ),
  };
};

/**
 * Gradient utilities
 */
export const gradients = {
  /**
   * Create a linear gradient
   */
  linear: (
    color1: string,
    color2: string,
    direction: GradientDirection = 'to-bottom-right'
  ): string => {
    return generateGradient(color1, color2, directionToAngle(direction));
  },

  /**
   * Create a radial gradient
   */
  radial: (
    color1: string,
    color2: string,
    shape: 'circle' | 'ellipse' = 'circle',
    position: string = 'center'
  ): string => {
    return generateRadialGradient(color1, color2, shape, position);
  },

  /**
   * Create a conic gradient
   */
  conic: (
    colors: string[],
    position: string = 'center',
    angle: string = '0deg'
  ): string => {
    const colorStops = colors.map((color, index) => {
      const position = (index / (colors.length - 1)) * 100;
      return `${color} ${position}%`;
    });
    return `conic-gradient(from ${angle} at ${position}, ${colorStops.join(', ')})`;
  },

  /**
   * Create a multi-stop gradient
   */
  multiStop: (
    stops: Array<{ color: string; position: string }>,
    direction: GradientDirection = 'to-bottom-right'
  ): string => {
    const stopStrings = stops.map(stop => `${stop.color} ${stop.position}`);
    return `linear-gradient(${directionToAngle(direction)}, ${stopStrings.join(', ')})`;
  },
};

/**
 * Common gradient patterns
 */
export const gradientPatterns = {
  /**
   * Subtle background gradient
   */
  subtleBackground: (colors: ThemeColors): string => {
    return gradients.linear(colors.background, colors.surface, 'to-bottom');
  },

  /**
   * Card hover gradient
   */
  cardHover: (colors: ThemeColors): string => {
    return gradients.linear(colors.surface, colors.surfaceHover, 'to-bottom');
  },

  /**
   * Button primary gradient
   */
  buttonPrimary: (colors: ThemeColors): string => {
    return gradients.linear(colors.primary, colors.primaryLight, 'to-bottom-right');
  },

  /**
   * Button secondary gradient
   */
  buttonSecondary: (colors: ThemeColors): string => {
    return gradients.linear(colors.secondary, colors.secondaryLight, 'to-bottom-right');
  },

  /**
   * Accent highlight gradient
   */
  accentHighlight: (colors: ThemeColors): string => {
    return gradients.linear(colors.accent, colors.accentLight, 'to-right');
  },

  /**
   * Glass morphism gradient
   */
  glass: (colors: ThemeColors): string => {
    return gradients.linear(
      'rgba(255, 255, 255, 0.25)',
      'rgba(255, 255, 255, 0.1)',
      'to-bottom'
    );
  },

  /**
   * Overlay gradient
   */
  overlay: (opacity: number = 0.5): string => {
    return gradients.linear(
      `rgba(0, 0, 0, ${opacity})`,
      `rgba(0, 0, 0, ${opacity * 0.5})`,
      'to-bottom'
    );
  },

  /**
   * Stat card gradients - colorful variations
   */
  statMembers: (): string => {
    return gradients.linear('#3b82f6', '#2563eb', '135deg');
  },

  statInstitutions: (): string => {
    return gradients.linear('#8b5cf6', '#7c3aed', '135deg');
  },

  statEvents: (): string => {
    return gradients.linear('#ec4899', '#db2777', '135deg');
  },

  statResources: (): string => {
    return gradients.linear('#10b981', '#059669', '135deg');
  },

  statNews: (): string => {
    return gradients.linear('#f59e0b', '#f97316', '135deg');
  },

  statPending: (): string => {
    return gradients.linear('#f59e0b', '#f97316', '135deg');
  },

  /**
   * Action-specific gradients
   */
  actionPrimary: (): string => {
    return gradients.linear('#3b82f6', '#2563eb', '135deg');
  },

  actionSecondary: (): string => {
    return gradients.linear('#8b5cf6', '#7c3aed', '135deg');
  },

  actionSuccess: (): string => {
    return gradients.linear('#10b981', '#059669', '135deg');
  },

  actionWarning: (): string => {
    return gradients.linear('#f59e0b', '#f97316', '135deg');
  },

  actionDanger: (): string => {
    return gradients.linear('#ef4444', '#dc2626', '135deg');
  },

  /**
   * Vibrant card header gradients
   */
  cardHeaderBlue: (): string => {
    return gradients.linear('#3b82f6', '#2563eb', 'to-right');
  },

  cardHeaderPurple: (): string => {
    return gradients.linear('#8b5cf6', '#7c3aed', 'to-right');
  },

  cardHeaderPink: (): string => {
    return gradients.linear('#ec4899', '#db2777', 'to-right');
  },

  cardHeaderGreen: (): string => {
    return gradients.linear('#10b981', '#059669', 'to-right');
  },

  cardHeaderOrange: (): string => {
    return gradients.linear('#f59e0b', '#f97316', 'to-right');
  },
};

export type Gradients = typeof gradients;
export type GradientPatterns = typeof gradientPatterns;
